import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { PrintDocumentOptionsDTO, PrintTemplateInListDTO, Res } from 'core/api';
import { PosScreenClassComponent, DialogIcon, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import { PrintFacade } from 'pos/facades';
import { Cols, Col } from 'core/ui';
import { Modal } from 'core/ui-next/modal';

type ContentProps = { initialContent: string } & Modal.Actions<string>;

type ContentState = { content: string };

class GetContentScreen extends PosScreenClassComponent<ContentProps, ContentState> {


	protected form = React.createRef<UI.Form<ContentState>>();

	protected getScreenId(): string {
		return 'GET_PRINT_CONTENT';
	}

	protected getTitle(): string {
		return Res.Web.GET_DOCUMENT_CONTENT_REPLACEMENT();
	}

	protected getImage(): string | SvgIcon {
		return Images.Edit;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({ onOK: this.onOk });
	}

	onOk = async (): Promise<void> => {
		const updated = await this.form.current!.updateModel();
		if (updated)
			this.close(this.state.content);
	};

	protected useKeypad() {
		return false;
	}

	protected getContent(): React.ReactNode {
		const { Form, TextBox } = UI.controlsFor<ContentState>();
		return <Form model={this.state} ref={this.form}>
			<DialogIcon src={Images.Edit} />
			<Cols>
				<Col width='*' className='m-t-sm'>
					<TextBox linkTo={x => x.content} lines={10} placeholder={Res.Web.GET_DOCUMENT_CONTENT_REPLACEMENT_PLACEHOLDER()} required />
				</Col>
			</Cols>
		</Form>;
	}

	createInitialState(props: ContentProps): Nullable<ContentState> {
		return { content: props.initialContent };
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}
}

async function getContent(previousContent: string): Promise<Result<string>> {
	return Modal.show(GetContentScreen, { initialContent: previousContent });
}



type Props = Context & Modal.Actions<void>;

type State = { template: Nullable<PrintTemplateInListDTO>, printer: string, content: string };

class PrintDocumentScreen extends PosScreenClassComponent<Props, State> {

	protected form = React.createRef<UI.Form<State>>();

	protected getScreenId(): string {
		return 'PRINT_DOCUMENT';
	}

	protected getTitle(): string {
		return Res.Web.PRINT_DOCUMENT();
	}

	protected getImage(): string | SvgIcon {
		return Images.PrintTicket;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			{ title: Res.Web.PRINT_DOCUMENT_REPLACE_CONTENT_BTN(), image: Images.Edit, action: this.updateContent },
			null,
			null,
			this.okButton(this.print)
		];
	}

	print = async (): Promise<void> => {
		const valid = await this.form.current!.updateModel();
		if (!valid)
			return;

		await this.props.print({
			printer: this.state.printer,
			printTemplate: this.state.template!,
			contentReplacement: this.state.content
		});

		this.close();
	};

	updateContent = async (): Promise<void> => {
		const result = await getContent(this.state.content);
		if (!result.ok)
			return;

		this.setState({ content: result.value });
	};

	protected useKeypad() {
		return false;
	}

	protected getContent(): React.ReactNode {
		const { Form, ComboBox } = UI.controlsFor<State>();

		return <Form small model={this.state} ref={this.form}>
			<DialogIcon src={Images.PrintTicket} />
			<Cols>
				<Col className='m-t-md' width='100%'>
					<ComboBox label={Res.Web.PRINT_DOCUMENT_TEMPLATES_LBL()} options={this.props.getOptions().ExistingTemplates} linkTo={x => x.template} required />
				</Col>
			</Cols>
			<Cols>
				<Col width='100%'>
					<ComboBox label={Res.Web.PRINT_DOCUMENT_PRINTER_LBL()} options={this.props.getOptions().InstalledPrinters} linkTo={x => x.printer} required />
				</Col>
			</Cols>
		</Form>;
	}

	createInitialState(props: Props): Nullable<State> {
		return {
			template: props.getOptions().Template,
			printer: props.getOptions().Printer,
			content: ''
		};
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}
}

type Context = {
	print(printParams: PrintFacade.PrintParams): Promise<void>,
	getOptions(): PrintDocumentOptionsDTO;
}

export async function printDocument(context: Context): Promise<Result> {
	return Modal.show(PrintDocumentScreen, { ...context });
}